import type { AppDispatch } from '../../data/store';
import authService from '../authService';
import { TripLegEventType, updateTripLeg } from './updateTripLeg';
import {
  ProviderNoteEventType,
  updateProviderNote,
} from './updateProviderNote';
import {
  InternalNoteEventType,
  updateInternalNote,
} from './updateInternalNote';
import { TaskEventType, updateTask } from './updateTask';
import { ReservationEventType, updateReservation } from './updateReservation';
import { ServicePlanEventType, updateServicePlan } from './updateServicePlan';
import { PassengerEventType, updatePassenger } from './updatePassenger';
import { AuthEventType, updateAuth } from './updateAuth';
import {
  DriverLocationEventType,
  updateDriverLocation,
} from './updateDriverLocation';
import { UserAccountEventType, updateUserAccount } from './updateUserAccount';
import {
  ProviderInvoiceEventType,
  updateProviderInvoice,
} from './updateProviderInvoice';

interface IWebSocketService {
  connect(dispatch: AppDispatch): void;
  disconnect(): void;
}

type WebSocketSessionInfo = {
  userAccountID: number | null;
  accessToken: string | null;
  accessTokenID: string | null;
};

type WebSocketLogLevel = 0 | 1 | 2 | 3;

class WebSocketService implements IWebSocketService {
  private readonly endPoint;
  private webSocket: WebSocket | null = null;
  private dispatch: AppDispatch | null = null;
  /**
   * Whether or not to log packet data that isn't handled in some way
   * 0 = off
   * 1 = display diagnostic websocket messages and critical warnings
   * 2 = display unhandled packets
   * 3 = display all incoming packets
   */
  private logLevel: WebSocketLogLevel = 0;
  private sessionInfo: WebSocketSessionInfo = {
    userAccountID: null,
    accessToken: null,
    accessTokenID: null,
  };
  private heartBeatInterval: number = 15 * 1000;
  private reconnectTimeout: number = 60 * 1000;
  private isHeartBeatRunning: boolean = false;
  private heartBeatTimer: NodeJS.Timeout | null = null;
  private isAlive: boolean = false;
  private lastPong: Date | null = null;

  /**
   * Constructor for the WebSocketService class.
   * @constructor
   */
  constructor() {
    const { __environment__ } = window as {
      __environment__: {
        REACT_APP_PUSH_NOTIFICATION_ENDPOINT: string;
        REACT_APP_WS_HEARTBEAT_INTERVAL?: number;
        REACT_APP_WS_RECONNECT_TIMEOUT?: number;
        REACT_APP_WS_DEBUG?: boolean;
      };
    };
    this.endPoint = __environment__.REACT_APP_PUSH_NOTIFICATION_ENDPOINT.trim();
    if (__environment__.REACT_APP_WS_HEARTBEAT_INTERVAL) {
      this.heartBeatInterval = __environment__.REACT_APP_WS_HEARTBEAT_INTERVAL;
    }
    if (__environment__.REACT_APP_WS_RECONNECT_TIMEOUT) {
      this.reconnectTimeout = __environment__.REACT_APP_WS_RECONNECT_TIMEOUT;
    }
    if (
      __environment__.REACT_APP_WS_DEBUG &&
      __environment__.REACT_APP_WS_DEBUG === true
    ) {
      this.logLevel = 2;
      // Expose some public methods for debugging in development environments
      (window as any).disconnectWebSocket = () => {
        this.disconnect();
      };
      (window as any).reconnectWebSocket = () => {
        this.reconnect();
      };
      (window as any).setWebSocketLogLevel = (logLevel: WebSocketLogLevel) => {
        this.setLogLevel(logLevel);
      };
    }
  }

  private updateTripLeg = updateTripLeg;
  private updateDriverLocation = updateDriverLocation;
  private updateReservation = updateReservation;
  private updateProviderNote = updateProviderNote;
  private updateInternalNote = updateInternalNote;
  private updateServicePlan = updateServicePlan;
  private updatePassenger = updatePassenger;
  private updateTask = updateTask;
  private updateAuth = updateAuth;
  private updateUserAccount = updateUserAccount;
  private updateProviderInvoice = updateProviderInvoice;

  /**
   * Debug Log for any messages that aren't currently being handled
   * @param detailType The type of message that was received, used to determine how to handle the payload
   * @param detail The payload received from the server
   */
  private logUnhandledMessage(detailType: string, detail: any) {
    if (this.logLevel > 1) {
      this.logSocketEvent('debug', `Unhandled Message ${detailType}`, detail);
    }
  }

  /**
   * Log a message to the console
   * @param type The type of message to log. Should be one of the following: debug, log, info, warn, error
   * @param message A custom message to represent the intent of the log
   * @param payload The message received from the server
   */
  private logSocketEvent(type: string, message: string, payload?: any) {
    /* eslint-disable no-console */
    let logFn: any = console.debug;
    /* eslint-disable no-console */
    if (type in console) {
      logFn = console[type as keyof Console];
    }
    if (this.logLevel > 0) {
      logFn.apply(console, [
        `WebSocket ${type}:`,
        message,
        payload,
        this.sessionInfo,
      ]);
    }
  }

  /**
   * Set the sessionInfo object. This is used to pass data from the Redux store to the WebSocketService
   * @param sessionInfo Partial of WebSocketSessionInfo object that you want to update
   */
  public setSessionInfo(sessionInfo: Partial<WebSocketSessionInfo>) {
    this.sessionInfo = {
      ...this.sessionInfo,
      ...sessionInfo,
    };
  }

  public refreshTokens() {
    authService.getTokens().subscribe((tokens) => {
      this.setSessionInfo({
        accessToken: tokens.accessToken,
        accessTokenID: tokens.idToken,
      });
    });
  }

  /**
   * Start the ping/pong heartbeat interval
   */
  private startHeartbeat() {
    this.refreshTokens();
    if (!this.sessionInfo.accessToken) return;
    if (this.heartBeatTimer) {
      clearTimeout(this.heartBeatTimer);
    }
    this.logSocketEvent('debug', 'Starting heartbeat');
    this.isHeartBeatRunning = true;
    const pongTime = new Date();
    this.lastPong = pongTime;
    this.sendHeartbeat();
  }

  /**
   * Stop the ping/pong heartbeat interval
   */
  private stopHeartbeat() {
    this.isHeartBeatRunning = false;
    this.lastPong = null;
  }

  /**
   * Send a ping/pong heartbeat message to the server
   * If the server doesn't respond in a reasonable amount of time, the connection is considered dead
   */
  private sendHeartbeat() {
    this.refreshTokens();
    if (!this.webSocket) return;
    if (this.webSocket.readyState !== 1) return;

    // if time since last pong is greater than reconnect timer, consider the connection dead
    if (
      this.isAlive &&
      this.lastPong &&
      this.lastPong.getTime() + this.reconnectTimeout < Date.now()
    ) {
      this.logSocketEvent('debug', 'Connection dead, reconnecting');
      this.reconnect();
      return;
    }
    // if websocket is not open or connecting, try reconnecting early
    if (
      this.webSocket.readyState !== WebSocket.OPEN &&
      this.webSocket.readyState !== WebSocket.CONNECTING
    ) {
      this.logSocketEvent(
        'debug',
        `Connection readyState ${this.webSocket.readyState}, reconnecting`,
      );
      this.reconnect();
      return;
    }
    this.heartBeatTimer = setTimeout(() => {
      if (
        this.isHeartBeatRunning &&
        this.webSocket?.readyState === WebSocket.OPEN
      ) {
        this.webSocket?.send('ping');
        this.sendHeartbeat();
      }
    }, this.heartBeatInterval);
  }

  /**
   * Send the authentication message to the server
   * No messages will arrive until this is sent. Should match the access token in the AuthService
   */
  private sendAuthenticationMessage() {
    // if the websocket is still connecting, try again later
    if (this.webSocket?.readyState === WebSocket.CONNECTING) {
      setTimeout(() => {
        this.sendAuthenticationMessage();
      }, 50);
      return;
    }
    if (this.sessionInfo.accessToken) {
      const message = {
        Authorization: `Bearer ${this.sessionInfo.accessToken}`,
        AuthorizationID: this.sessionInfo.accessTokenID,
      };
      this.webSocket?.send(JSON.stringify(message));
    }
  }

  /**
   * Reconnect to the WebSocket endpoint.
   */
  public reconnect() {
    this.stopHeartbeat();
    this.disconnect();
    this.isAlive = false;

    setTimeout(() => {
      if (this.dispatch) {
        this.connect(this.dispatch, false);
      }
    }, 1000);
  }

  /**
   * Initialize the connection to the WebSocket endpoint. All message handling is done internally here.
   * @param dispatch
   */
  public connect(dispatch: AppDispatch, disconnect: boolean = true) {
    if (disconnect) {
      this.disconnect();
    }
    this.refreshTokens();

    this.dispatch = dispatch;
    this.webSocket = new WebSocket(this.endPoint);

    this.webSocket.onopen = () => {
      this.logSocketEvent('debug', 'WebSocket opened');
      this.isAlive = true;
      this.startHeartbeat();
      this.sendAuthenticationMessage();
    };

    this.webSocket.onclose = () => {
      this.logSocketEvent('log', 'WebSocket closed');
      this.reconnect();
    };

    this.webSocket.onerror = (error) => {
      this.logSocketEvent('error', 'WebSocket error:', error);
    };

    this.webSocket.onmessage = (event: any) => {
      // Heartbeat response
      if (event.type === 'message' && event.data === 'pong') {
        this.isAlive = true;
        const pongTime = new Date();
        this.lastPong = pongTime;
        return;
      }

      // checking for connection established message
      if (
        event.type === 'message' &&
        event?.data === 'connection established'
      ) {
        return; // do nothing for this message
      }

      // The server sent us a ping message, respond with a pong
      if (event.type === 'message' && event.data === 'ping') {
        this.webSocket?.send('pong');
        return;
      }

      if (event.type === 'message' && event.data) {
        try {
          const { detail, detailType } = JSON.parse(event.data);
          if (this.logLevel > 2) {
            this.logSocketEvent(
              'debug',
              `Packet received (${detailType}):`,
              detail,
            );
          }

          if (typeof detailType === 'string') {
            // Handle Auth Events
            if (detailType in AuthEventType) {
              this.updateAuth(
                this.sessionInfo,
                dispatch,
                detailType as AuthEventType,
                detail,
              );
            }

            // Handle DriverLocation Events
            if (detailType in DriverLocationEventType) {
              this.updateDriverLocation(
                this.sessionInfo,
                dispatch,
                detailType as DriverLocationEventType,
                detail,
              );
            }

            // Handle TripLeg Events
            if (detailType in TripLegEventType) {
              this.updateTripLeg(
                this.sessionInfo,
                dispatch,
                detailType as TripLegEventType,
                detail,
              );
            }

            // Handle Service Plan Events
            else if (detailType in ServicePlanEventType) {
              this.logUnhandledMessage(detailType, detail);
            }

            // Handle Reservation Events
            else if (detailType in ReservationEventType) {
              this.updateReservation(
                this.sessionInfo,
                dispatch,
                detailType,
                detail,
              );
            }

            // Handle Provider Note Events
            else if (detailType in ProviderNoteEventType) {
              this.updateProviderNote(
                this.sessionInfo,
                dispatch,
                detailType,
                detail,
              );
            }

            // Handle Internal Note Events
            else if (detailType in InternalNoteEventType) {
              this.updateInternalNote(
                this.sessionInfo,
                dispatch,
                detailType,
                detail,
              );
            }

            // Handle Passenger Events
            else if (detailType in PassengerEventType) {
              this.updatePassenger(
                this.sessionInfo,
                dispatch,
                detailType,
                detail,
              );
            }

            // Handle Task Events
            else if (detailType in TaskEventType) {
              this.logUnhandledMessage(detailType, detail);
            }

            // Handle Feature Flag Events
            else if (detailType in UserAccountEventType) {
              this.updateUserAccount(
                this.sessionInfo,
                dispatch,
                detailType,
                detail,
              );
            }

            // Handle Provider Invoice Events
            else if (detailType in ProviderInvoiceEventType) {
              this.updateProviderInvoice(
                this.sessionInfo,
                dispatch,
                detailType,
                detail,
              );
            }

            // Log a message in development for unhandled messages
            else {
              this.logUnhandledMessage(detailType, detail);
            }
          }
        } catch {
          // Nothing to do here. It just means that the event is not one of ours and we can ignore it.
        }
      }
    };
  }

  /**
   * Disconnect from the WebSocket endpoint.
   */
  public disconnect() {
    if (this.webSocket) {
      this.webSocket.close();
      this.webSocket = null;
    }
  }

  /**
   * Set the log level for the WebSocketService
   * @param logLevel The log level to set. Must be one of the following values: 0, 1, 2, 3, 4
   */
  public setLogLevel(logLevel: WebSocketLogLevel) {
    this.logLevel = logLevel;
  }
}

const webSocketService = new WebSocketService();

export type { WebSocketSessionInfo };
export { webSocketService };
